import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { BorLetterResponse } from "../../../client";
import { ROUTES } from "../../../routes";
import { ensureUtc, formatTimestamp } from "../utils";
import {
  borOtherReportId,
  borSideForReport,
  getBorSteps,
  useBorLettersByReport,
  useMarkBorStep,
} from "./data";
import styles from "./BorPanel.module.css";

type BorPanelProps = {
  reportId: string;
};

/**
 * Panel shown on the report detail page listing all BOR letters
 * for this report, with a checklist of steps and deadlines.
 */
export const BorPanel: React.FC<BorPanelProps> = ({ reportId }) => {
  const { data: borLetters, isLoading } = useBorLettersByReport(reportId);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>Loading BOR data...</div>
      </div>
    );
  }

  if (!borLetters || borLetters.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          No BOR letters for this submission.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {borLetters.map((bor) => (
        <BorLetterCard key={bor.id} bor={bor} reportId={reportId} />
      ))}
    </div>
  );
};

// ── Single BOR Letter Card ──

type BorLetterCardProps = {
  bor: BorLetterResponse;
  reportId: string;
};

const BorLetterCard: React.FC<BorLetterCardProps> = ({ bor, reportId }) => {
  const navigate = useNavigate();
  const markStep = useMarkBorStep();
  const side = borSideForReport(bor, reportId);
  const otherReportId = borOtherReportId(bor, reportId);
  const steps = getBorSteps(bor);

  const navigateToOther = () => {
    navigate(ROUTES.DASHBOARD_REPORT(otherReportId));
  };

  return (
    <div>
      <div className={styles.header}>
        <span className={styles.title}>
          {bor.is_resolved
            ? bor.rescind_letter_received_at
              ? "BOR Rescinded"
              : "BOR Finalized"
            : "BOR In Process"}
        </span>
      </div>

      <div className={styles.meta}>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel}>This report:</span>
          <span>
            {side === "cleared" ? "Cleared broker" : "Incoming broker"}
          </span>
        </div>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel}>
            {side === "cleared" ? "Incoming:" : "Cleared:"}
          </span>
          <span className={styles.linkedReport} onClick={navigateToOther}>
            {otherReportId}
          </span>
        </div>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel}>Received:</span>
          <span>{formatTimestamp(bor.received_at)}</span>
        </div>
        {bor.effective_date && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Effective date:</span>
            <span>{formatTimestamp(bor.effective_date)}</span>
          </div>
        )}
      </div>

      <DeadlineInfo bor={bor} />

      <div className={styles.checklist}>
        {steps.map((step) => {
          const isDone = step.timestamp !== null;

          // If BOR was rescinded, don't show "finalized" step and vice versa
          if (
            step.key === "finalized_at" &&
            bor.rescind_letter_received_at !== null
          ) {
            return null;
          }
          if (
            step.key === "rescind_letter_received_at" &&
            bor.finalized_at !== null
          ) {
            return null;
          }

          // Don't show resolution notification steps until resolved
          if (
            (step.key === "outgoing_broker_resolution_sent_at" ||
              step.key === "incoming_broker_resolution_sent_at") &&
            !bor.is_resolved
          ) {
            return null;
          }

          return (
            <div
              key={step.key}
              className={`${styles.step} ${isDone ? styles.stepDone : styles.stepPending}`}
            >
              <i
                className={`pi ${isDone ? "pi-check-circle" : "pi-circle"} ${styles.checkIcon} ${isDone ? styles.checkDone : styles.checkPending}`}
              />
              <span>{step.label}</span>
              {isDone && step.timestamp && (
                <span className={styles.timestamp}>
                  {formatTimestamp(step.timestamp)}
                </span>
              )}
              {!isDone && (
                <Button
                  className={styles.markButton}
                  label="Mark done"
                  severity="secondary"
                  text
                  raised
                  size="small"
                  loading={markStep.isPending}
                  onClick={() =>
                    markStep.mutate({ borId: bor.id, step: step.key })
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      {bor.notes && (
        <div className={styles.meta}>
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Notes:</span>
            <span>{bor.notes}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Deadline display ──

const DeadlineInfo: React.FC<{ bor: BorLetterResponse }> = ({ bor }) => {
  if (bor.is_resolved) return null;

  const now = new Date();

  // Show notification SLA deadline if outgoing broker not yet notified
  if (!bor.outgoing_broker_notified_at && bor.notification_sla_deadline) {
    const deadline = new Date(ensureUtc(bor.notification_sla_deadline));
    const isOverdue = now > deadline;
    return (
      <div
        className={`${styles.deadline} ${isOverdue ? styles.deadlineExpired : styles.deadlineWarning}`}
      >
        <i className="pi pi-clock" style={{ fontSize: "12px" }} />
        {isOverdue
          ? "Overdue: Outgoing broker notification past 24hr SLA"
          : `Notify outgoing broker by ${formatTimestamp(bor.notification_sla_deadline)}`}
      </div>
    );
  }

  // Show rescind deadline if set
  if (bor.rescind_deadline) {
    const deadline = new Date(ensureUtc(bor.rescind_deadline));
    const isOverdue = now > deadline;
    const isClose =
      !isOverdue && deadline.getTime() - now.getTime() < 24 * 60 * 60 * 1000;
    return (
      <div
        className={`${styles.deadline} ${isOverdue ? styles.deadlineExpired : isClose ? styles.deadlineWarning : styles.deadlineOk}`}
      >
        <i className="pi pi-clock" style={{ fontSize: "12px" }} />
        {isOverdue
          ? "Rescind period expired — ready to finalize"
          : `Rescind deadline: ${formatTimestamp(bor.rescind_deadline)}`}
      </div>
    );
  }

  return null;
};
